import { useSearchParams, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import Image from '@/components/atoms/Image';
import { AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ECreateThemeModalType } from '@/enums';
import useOrganizationRoles from '@/hooks/useOrganizationRoles';
import useTranslation from '@/hooks/useTranslation';

interface IBrandingInputsProps {
  primary1Color: string;
  primary2Color: string;
  secondaryColor: string;
  backgroundColor: string;
  progressBarColor: string;
  consentBackgroundColor: string;
  setPrimary1Color: React.Dispatch<React.SetStateAction<string>>;
  setPrimary2Color: React.Dispatch<React.SetStateAction<string>>;
  setSecondaryColor: React.Dispatch<React.SetStateAction<string>>;
  setBackgroundColor: React.Dispatch<React.SetStateAction<string>>;
  setProgressBarColor: React.Dispatch<React.SetStateAction<string>>;
  setConsentBackgroundColor: React.Dispatch<React.SetStateAction<string>>;
  setOpenModal: (open: boolean) => void;
  handleExitModal: () => void;
  brandingName: string;
  setBrandingName: React.Dispatch<React.SetStateAction<string>>;
  onSubmit: () => void;
  themeIsDefault: boolean;
  handleLogo: string | null;
  setHandleLogo: React.Dispatch<React.SetStateAction<string | null>>;
  handleFavicon: string | null;
  setHandleFavicon: React.Dispatch<React.SetStateAction<string | null>>;
  logoFile: File | null;
  setLogoFile: React.Dispatch<React.SetStateAction<File | null>>;
  faviconFile: File | null;
  setFaviconFile: React.Dispatch<React.SetStateAction<File | null>>;
}

function ProjectBrandingInputs({
  primary1Color,
  primary2Color,
  secondaryColor,
  backgroundColor,
  progressBarColor,
  consentBackgroundColor,
  setPrimary1Color,
  setPrimary2Color,
  setSecondaryColor,
  setBackgroundColor,
  setProgressBarColor,
  setConsentBackgroundColor,
  setOpenModal,
  handleExitModal,
  brandingName,
  setBrandingName,
  onSubmit,
  themeIsDefault,
  handleLogo,
  setHandleLogo,
  handleFavicon,
  setHandleFavicon,
  setLogoFile,
  setFaviconFile,
}: IBrandingInputsProps) {
  const [searchParams] = useSearchParams();
  const modalMode = searchParams.get('mode');
  const { isUserAdmin, isUserOwner } = useOrganizationRoles();
  const { lang } = useTranslation();

  const isNotAdminOrOwner = !isUserAdmin && !isUserOwner;

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!['image/png', 'image/svg+xml'].includes(file.type)) {
        toast.info(lang.get('msg.pleaseUploadPngOrSvgFile'));
        // Reset the input value so the same file can be selected again
        e.target.value = '';
        return;
      }
      const fileURL = URL.createObjectURL(file);
      setHandleLogo(fileURL);
      setLogoFile(file);
    }
    // Reset the input value after processing
    e.target.value = '';
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type - check both MIME type and file extension
      const allowedMimeTypes = [
        'image/x-icon',
        'image/vnd.microsoft.icon',
        'image/ico',
        'application/x-ico',
      ];
      const isValidIconFile =
        allowedMimeTypes.includes(file.type) || file.name.toLowerCase().endsWith('.ico');

      if (!isValidIconFile) {
        toast.info(lang.get('msg.pleaseUploadIcoFile'));
        // Reset the input value so the same file can be selected again
        e.target.value = '';
        return;
      }
      const fileURL = URL.createObjectURL(file);
      setHandleFavicon(fileURL);
      setFaviconFile(file);
    }
    // Reset the input value after processing
    e.target.value = '';
  };

  const handleRemoveLogo = () => {
    // Just reset the UI state - don't call backend until save
    setHandleLogo(null);
    setLogoFile(null);
  };

  const handleRemoveFavicon = () => {
    // Just reset the UI state - don't call backend until save
    setHandleFavicon(null);
    setFaviconFile(null);
  };

  return (
    <div className="flex flex-col justify-between w-full h-full gap-2">
      <div className="flex flex-col h-full gap-5 pb-2 overflow-y-auto text-sm">
        <label className="flex flex-col w-full gap-1 font-medium">
          {lang.get('msg.name')}
          :
          <Input
            type="text"
            value={brandingName}
            onChange={(e) => setBrandingName(e.target.value)}
            placeholder="Enter name"
            disabled={themeIsDefault}
          />
        </label>
        <div className="flex items-center w-full gap-3">
          <label className="flex flex-col w-full gap-1 font-medium">
            {lang.get('msg.progressBarAndMicrophone')}
            :
            <Input
              type="color"
              value={primary1Color}
              onChange={(e) => setPrimary1Color(e?.target?.value)}
              disabled={themeIsDefault}
            />
          </label>
          <label className="flex flex-col w-full gap-1 font-medium">
            {lang.get('msg.participantMessage')}
            :
            <Input
              type="color"
              value={primary2Color}
              onChange={(e) => setPrimary2Color(e?.target?.value)}
              disabled={themeIsDefault}
            />
          </label>
        </div>
        <div className="flex items-center w-full gap-3">
          <label className="flex flex-col w-full gap-1 font-medium">
            {lang.get('msg.interviewerMessage')}
            :
            <Input
              type="color"
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e?.target?.value)}
              disabled={themeIsDefault}
            />
          </label>
          <label className="flex flex-col w-full gap-1 font-medium">
            {lang.get('msg.background')}
            :
            <Input
              type="color"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e?.target?.value)}
              disabled={themeIsDefault}
            />
          </label>
        </div>
        <div className="flex items-center w-full gap-3">
          <label className="flex flex-col w-full gap-1 font-medium">
            {lang.get('msg.progressBarBackground')}
            :
            <Input
              type="color"
              value={progressBarColor}
              onChange={(e) => setProgressBarColor(e?.target?.value)}
              disabled={themeIsDefault}
            />
          </label>
          <label className="flex flex-col w-full gap-1 font-medium">
            {lang.get('msg.consentScreenBackground')}
            :
            <Input
              type="color"
              value={consentBackgroundColor}
              onChange={(e) => setConsentBackgroundColor(e?.target?.value)}
              disabled={themeIsDefault}
            />
          </label>
        </div>
        {!themeIsDefault && (
          <div className="flex items-start w-full gap-3">
            <div className="flex flex-col w-full gap-1">
              <label className="flex flex-col w-full gap-1 font-medium">
                {lang.get('msg.companyLogo')} ({lang.get('msg.optional')}):
                <Input
                  type="file"
                  onChange={handleLogoChange}
                  accept=".png,.svg"
                  className="cursor-pointer"
                />
              </label>
              <span className="text-xs text-gray-500">{lang.get('msg.pngOrSvgFilesOnly')}</span>
              {handleLogo && (
                <div className="mt-2 flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg">
                  <Image src={handleLogo} alt="Company Logo" className="object-contain h-8" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleRemoveLogo}
                    disabled={isNotAdminOrOwner}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-col w-full gap-1">
              <label className="flex flex-col w-full gap-1 font-medium">
                {lang.get('msg.favicon')} ({lang.get('msg.optional')}):
                <Input
                  type="file"
                  onChange={handleFaviconChange}
                  accept=".ico,.ICO"
                  className="cursor-pointer"
                />
              </label>
              <span className="text-xs text-gray-500">{lang.get('msg.icoFilesOnly')}</span>
              {handleFavicon && (
                <div className="mt-2 flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <img
                      src={handleFavicon}
                      alt="Favicon"
                      className="object-contain h-8 w-8 border border-gray-300 rounded"
                      onError={(e) => {
                        // Fallback if ICO doesn't display - show a generic icon
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden h-8 w-8 border border-gray-300 rounded flex items-center justify-center bg-gray-100">
                      <span className="text-xs text-gray-500">ICO</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleRemoveFavicon}
                    disabled={isNotAdminOrOwner}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center w-full gap-4">
        <AlertDialogCancel
          className="w-full"
          onClick={() => {
            setOpenModal(false);
            handleExitModal();
          }}
        >
          {lang.get('msg.cancel')}
        </AlertDialogCancel>
        {modalMode === ECreateThemeModalType.CREATE && !themeIsDefault && !isNotAdminOrOwner && (
          <AlertDialogAction className="w-full" onClick={onSubmit}>
            {lang.get('msg.create')}
          </AlertDialogAction>
        )}
        {modalMode === ECreateThemeModalType.EDIT && !themeIsDefault && !isNotAdminOrOwner && (
          <AlertDialogAction className="w-full" onClick={onSubmit}>
            {lang.get('msg.save')}
          </AlertDialogAction>
        )}
      </div>
    </div>
  );
}

export default ProjectBrandingInputs;
